import { SERVER_LOCATION } from "../config.js"
import manageErrors from "../lib/error-management"
import { emitSnackbar } from "../lib/snackbar-emitter"
import treatResponse from "../lib/treat-response.ts"

export const OTP_REQUEST_COOLDOWN_DETAIL = 'Please wait a few minutes before requesting again.'
export const OTP_REQUEST_COOLDOWN_MESSAGE = 'Aguarde alguns minutos antes de solicitar um novo código.'
const OTP_SESSION_STORAGE_KEY = 'kwik_scout_otp_session'

const normalizeTelephone = (telephone) => String(telephone ?? '').replace(/\D/g, '')

const canUseBrowserStorage = () => typeof window !== 'undefined' && Boolean(window.sessionStorage)

const persistOtpSessionData = (sessionData) => {
	if (!canUseBrowserStorage())
		return

	window.sessionStorage.setItem(OTP_SESSION_STORAGE_KEY, JSON.stringify(sessionData))
}

const getStoredOtpSessionData = () => {
	if (!canUseBrowserStorage())
		return null

	const rawSessionData = window.sessionStorage.getItem(OTP_SESSION_STORAGE_KEY)
	if (!rawSessionData)
		return null

	try {
		return JSON.parse(rawSessionData)
	} catch {
		return null
	}
}

export const clearStoredOtpSessionData = () => {
	if (!canUseBrowserStorage())
		return

	window.sessionStorage.removeItem(OTP_SESSION_STORAGE_KEY)
}

const formatOtpWaitTime = (seconds) => {
	const minutes = Math.floor(seconds / 60)
	const remainingSeconds = seconds % 60

	return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

export const stampOtpSessionData = (sessionData = {}, telephone) => {
	const stampedSessionData = {
		...sessionData,
		requested_at_ms: Date.now(),
		requested_telephone: normalizeTelephone(telephone ?? sessionData?.telephone),
	}

	persistOtpSessionData(stampedSessionData)
	return stampedSessionData
}

const resolveOtpSessionData = (sessionData, telephone) => {
	const normalizedRequestTelephone = normalizeTelephone(telephone)
	const currentTelephone = normalizeTelephone(sessionData?.requested_telephone ?? sessionData?.telephone)

	if (sessionData?.retry_timeout && (!normalizedRequestTelephone || normalizedRequestTelephone === currentTelephone))
		return sessionData

	const storedSessionData = getStoredOtpSessionData()
	if (!storedSessionData)
		return sessionData

	const storedTelephone = normalizeTelephone(storedSessionData?.requested_telephone ?? storedSessionData?.telephone)
	if (normalizedRequestTelephone && storedTelephone && normalizedRequestTelephone !== storedTelephone)
		return sessionData

	return storedSessionData
}

export const getOtpRemainingSeconds = (sessionData, timeoutField = 'retry_timeout') => {
	const totalSeconds = Number(sessionData?.[timeoutField] ?? 0)
	const requestedAt = Number(sessionData?.requested_at_ms ?? 0)

	if (!Number.isFinite(totalSeconds) || totalSeconds <= 0)
		return 0

	if (!requestedAt)
		return totalSeconds

	const elapsedSeconds = Math.floor((Date.now() - requestedAt) / 1000)
	return Math.max(totalSeconds - elapsedSeconds, 0)
}

export const getOtpRequestCooldownMessage = (sessionData, telephone) => {
	const resolvedSessionData = resolveOtpSessionData(sessionData, telephone)
	const remainingSeconds = getOtpRemainingSeconds(resolvedSessionData)

	if (remainingSeconds <= 0)
		return OTP_REQUEST_COOLDOWN_MESSAGE

	return `Aguarde mais ${formatOtpWaitTime(remainingSeconds)} para solicitar um novo código.`
}

export const isOtpRequestCooldownError = (response) => (
	response?.status === 400 && response?.data?.detail === OTP_REQUEST_COOLDOWN_DETAIL
)


export const requestOtp = async (state, sessionId=null) => {

	const response = await fetch(`${SERVER_LOCATION}/auth/otp/`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: (sessionId !== null) 
			? JSON.stringify({ ...state, sessionId }) 
			: JSON.stringify({ ...state }),
	})
	const res = await treatResponse(response)
	return res

}

export const getSessionInfo = async (sessionId) => {
	const response = await fetch(`${SERVER_LOCATION}/auth/otp/session/${sessionId}/`)
	if (response.status === 200)
		return await response.json()
	else 
		emitSnackbar({message: manageErrors(response), severity: 'error'})
	return
}

export const verifyOtp = async (sessionId, otp, login) => {
	const response = await fetch(`${SERVER_LOCATION}/auth/otp/verify/`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		}, 
		body: JSON.stringify({ 
			"session_id": sessionId, 
			"code": otp,
			"login": login
		}),
	})

	return await treatResponse(response)
}


export const saveScoutSignupLead = async (state) => {
	const response = await fetch(`${SERVER_LOCATION}/api/enterprises/save_scout_signup_lead/`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ ...state }),
	})

	return await treatResponse(response)
}


export const createTrial = async (sessionId, data) => {
	const response = await fetch(`${SERVER_LOCATION}/api/enterprises/start_scout_trial/`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		}, 
		body: JSON.stringify({ 
			"session_id": sessionId, 
			...data
		}),
	})

	return await treatResponse(response)
}
